import { FormControlLabel, Radio, RadioGroup, useEventCallback } from '@mui/material';
import { FormControl, Grid } from '@mui/material';
import { FormikErrors, FormikTouched } from 'formik';
import { Fragment, memo } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import { DateTime } from 'luxon';
import ForeignStateList, { ForeignStateVM } from 'shell/components/shared/foreign-state-list';
import MunicipalityForeignList from 'shell/components/shared/municipality-foreign-list';
import { noop } from 'shell/utils/noop';
import { AccordionOriginResearch } from 'practice/view-models/origin-research';
import { Origin } from 'practice/constants/origin';

interface OriginFormProps {
  values?: AccordionOriginResearch;
  onChange: (
    key: keyof AccordionOriginResearch,
    value: AccordionOriginResearch[keyof AccordionOriginResearch]
  ) => void;
  onChangeFields: (values: AccordionOriginResearch) => void;
  disabled?: boolean;
  touched?: FormikTouched<AccordionOriginResearch>;
  errors?: FormikErrors<AccordionOriginResearch>;
}

const OriginFormComponent = ({
  values,
  onChange,
  onChangeFields,
  errors,
  touched,
}: OriginFormProps): React.ReactElement => {
  const handleMunicipalityListChange = useEventCallback(
    (municipality: MunicipalityAnprVM | null) => {
      onChange('municipality', municipality);
    }
  );

  const handleForeignStateListChange = useEventCallback(
    (state: ForeignStateVM | null | undefined) => {
      onChange('state', state);
    }
  );

  const handleForeignMunicipalityChange = useEventCallback(
    (municipality: MunicipalityAnprVM | null) => {
      onChange('stateLocation', municipality);
    }
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Selezione Provenienza"
            id="isForeign"
            value={values?.isForeign || 'false'}
            onChange={(event) => {
              onChangeFields({
                isForeign: event.target.value as Origin,
                municipality: null,
                location: '',
                state: null,
                stateLocation: null,
              });
            }}
          >
            <FormControlLabel
              value={Origin.Italian}
              control={<Radio />}
              label="Provenienza Italia"
              disabled={values?.irreperibile}
            />
            <FormControlLabel
              value={Origin.Foreign}
              control={<Radio />}
              disabled={values?.irreperibile}
              label="Provenienza Estero"
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      {values?.isForeign === Origin.Italian ? (
        <Fragment>
          <Grid item xs={12} md={6}>
            <MunicipalityList
              value={values?.municipality || null}
              onChange={handleMunicipalityListChange}
              label="Comune"
              fromDate={DateTime.now()}
              disabled={values?.irreperibile}
              error={
                (touched?.municipality as unknown as FormikTouched<MunicipalityAnprVM>) &&
                Boolean(errors?.municipality as FormikErrors<MunicipalityAnprVM>)
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <PmiTextField
              aria-label="Provincia"
              label="Provincia"
              id="province"
              name="province"
              fullWidth
              type="text"
              variant="outlined"
              value={values?.municipality?.province || ''}
              onChange={noop}
              disabled
            />
          </Grid>
        </Fragment>
      ) : values?.isForeign === Origin.Foreign ? (
        <Fragment>
          <Grid item xs={12} md={6}>
            <ForeignStateList
              value={values?.state || null}
              onChange={handleForeignStateListChange}
              disabled={values?.irreperibile}
              label="Stato"
              id="foreignState"
              error={
                (touched?.state as unknown as FormikTouched<ForeignStateVM>) &&
                Boolean(errors?.state as FormikErrors<ForeignStateVM>)
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <MunicipalityForeignList
              value={values?.stateLocation || null}
              onChange={handleForeignMunicipalityChange}
              label="Comune"
              id="foreignMunicipality"
              disabled={!values?.state?.id || values?.irreperibile}
              foreignStateIstatCode={values?.state?.id}
              error={
                touched?.stateLocation &&
                Boolean(errors?.stateLocation as FormikErrors<MunicipalityAnprVM>)
              }
            />
          </Grid>
        </Fragment>
      ) : undefined}
    </Grid>
  );
};

OriginFormComponent.displayName = 'Origin data form component';

const OriginForm = memo(OriginFormComponent);

export default OriginForm;
